import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  InputNumber,
  Select,
  DatePicker,
  Upload,
  Row,
  Col,
  Typography,
  Divider,
  message
} from "antd";
import { PlusOutlined, MinusCircleOutlined, CloseOutlined, CheckOutlined } from "@ant-design/icons";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { addQuotation } from "../../../../../Store/Slices/Finance/Quotations/quotationThunks";
import { useNavigate } from "react-router-dom";
import Layout from "../../../../../Components/Common/Layout";
import DashLayout from "../../../../../Components/Admin/AdminDashLayout";
import { useTranslation } from "react-i18next";
import useCloudinaryRawUpload from "../../../../../Hooks/CommonHooks/useCloudinaryRawUpload";


const { Title } = Typography;
const CLOUDINARY_UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_PRESET;
const CLOUDINARY_FOLDER = "Quotations";
const CreateQuotationAntForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [readOnly, setReadOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const { selectedQuotation } = useSelector(state => state.admin.quotations);
  const {
    uploadFile,
    uploadProgress,
    error: uploadError,
    resetUpload,
  } = useCloudinaryRawUpload(CLOUDINARY_UPLOAD_PRESET, CLOUDINARY_FOLDER);
  const { t } = useTranslation("prtFinance")
  const formatDate = (date) => moment(date).format("YYYY-MM-DD");

  const [file, setFile] = useState(null);
  const [cloudinaryResult, setCloudinaryResult] = useState(null);
  const [isFileConfirmed, setIsFileConfirmed] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setUploading(true);
    setIsFileConfirmed(false);

    const result = await uploadFile(selectedFile);
    setUploading(false);
    if (result) {
      setCloudinaryResult(result);
      message.success("File uploaded successfully!");
    } else {
      message.error("Upload failed!");
    }
  };


  const formattedQuotation = {
    receiver: selectedQuotation?.receiver || {
      name: "",
      email: "",
      address: "",
      phone: "",
    },
    lineItems: selectedQuotation?.lineItems || [
      { revenueType: "", subCategory: "", description: "", quantity: 1, amount: 0, },
    ],
    date: selectedQuotation?.date ? moment(selectedQuotation.date) : null,
    dueDate: selectedQuotation?.dueDate ? moment(selectedQuotation.dueDate) : null,
    purpose: selectedQuotation?.purpose || "",
    status: selectedQuotation?.status || "pending",
    total_amount: selectedQuotation?.total_amount || 0,
    tax: selectedQuotation?.tax || 0,
    discountType: selectedQuotation?.discountType || "percentage",
    discount: selectedQuotation?.discount || 0,
    final_amount: selectedQuotation?.final_amount || 0,
    document: selectedQuotation?.document || null,
    paymentMode: selectedQuotation?.paymentMode || "",
    paymentStatus: selectedQuotation?.paymentStatus || "",
    remainingAmount: selectedQuotation?.remainingAmount || 0,
    remark: selectedQuotation?.remark || "",
    govtRefNumber: selectedQuotation?.govtRefNumber || ""
  };

  useEffect(() => {
    form?.setFieldsValue(formattedQuotation);
    setReadOnly(!!selectedQuotation);
  }, [selectedQuotation]);

  useEffect(() => {
    if (selectedQuotation?.document) {
      setCloudinaryResult(selectedQuotation.document);
      setIsFileConfirmed(true);
    }
  }, [selectedQuotation]);


  // useEffect(() => {
  //   const unsubscribe = form.subscribe(({ values }) => {
  //     const updatedItems = values?.lineItems?.map((item) => ({
  //       ...item,
  //       quantityAmount: (item.quantity || 0) * (item.amount || 0),
  //     }));
  //     form?.setFieldsValue({ lineItems: updatedItems });
  //   });

  //   return () => unsubscribe?.();
  // }, [form]);

  const onFinish = async (values) => {
    const payload = {
      ...values,
      document: cloudinaryResult,
      date: values.date.format("YYYY-MM-DD"),
      dueDate: values.dueDate.format("YYYY-MM-DD"),
    };

    try {
      setLoading(true);
      await dispatch(addQuotation(payload)).unwrap();
      navigate("/finance/quotations/quotations-list");
    } catch (err) {
      message.error("Failed to save quotation");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    const unsubscribe = form?.watch?.((changedValues, allValues) => {
      if (changedValues?.lineItems) {
        const updatedItems = allValues.lineItems.map((item) => ({
          ...item,
          quantityAmount: (item.quantity || 0) * (item.amount || 0),
        }));
        form.setFieldsValue({ lineItems: updatedItems });
      }
    });
    return () => unsubscribe?.();
  }, [form]);

  return (
    <Layout>
      <DashLayout>
        <div className="p-6 min-h-screen bg-white">
          <Title level={3}>{readOnly ? t("View Quotation") : t("Create Quotation")}</Title>
          <Divider />

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            disabled={readOnly}
            onValuesChange={(changedValues, allValues) => {
              if (changedValues?.lineItems) {
                const updatedItems = allValues?.lineItems?.map((item) => ({
                  ...item,
                  quantityAmount: (item?.quantity || 0) * (item?.amount || 0),
                }));
                form.setFieldsValue({ lineItems: updatedItems });
              }
            }}
          >

            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Form.Item label={t("Receiver Name")} name={["receiver", "name"]} rules={[{ required: true }]}>
                  <Input placeholder={t("Enter receiver's name")} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={t("Address")} name={["receiver", "address"]}>
                  <Input placeholder={t("Enter address")} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={t("Phone")} name={["receiver", "phone"]}>
                  <Input placeholder={t("Enter phone number")} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={8}>
                <Form.Item label={t("Email")} name={["receiver", "email"]}>
                  <Input type="email" placeholder={t("Enter email")} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={t("Purpose")} name="purpose" rules={[{ required: true }]}>
                  <Input placeholder={t("Enter purpose")} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={t("Quotation Date")} name="date" rules={[{ required: true }]}>
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" className="px-10">{t("Items")}</Divider>
            <Form.List name="lineItems">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={[16, 16]} key={key} align="middle">
                      <Col span={5}>
                        <Form.Item {...restField} name={[name, "category"]} label={t("Category")}>
                          <Input placeholder={t("Category")} />
                        </Form.Item>
                      </Col>
                      <Col span={5}>
                        <Form.Item {...restField} name={[name, "description"]} label={t("Description")}>
                          <Input placeholder={t("Description")} />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item {...restField} name={[name, "quantity"]} label={t("Qty")}>
                          <InputNumber min={1} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={3}>
                        <Form.Item {...restField} name={[name, "amount"]} label={t("Rate")}>
                          <InputNumber min={0} style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item {...restField} name={[name, "quantityAmount"]} label={t("Total")}>
                          <InputNumber disabled style={{ width: "100%" }} />
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        {!readOnly && <MinusCircleOutlined onClick={() => remove(name)} />}
                      </Col>
                    </Row>
                  ))}
                  {!readOnly && (
                    <Form.Item>
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        {t("Add Item")}
                      </Button>
                    </Form.Item>
                  )}
                </>
              )}
            </Form.List>

            <Divider />

            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Form.Item label={t("Due Date")} name="dueDate" rules={[{ required: true }]}>
                  <DatePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={t("Tax (%)")} name="tax">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={t("Discount Type")} name="discountType" rules={[{ required: true }]}>
                  <Select>
                    <Select.Option value="percentage">{t("Percentage")}</Select.Option>
                    <Select.Option value="amount">{t("Amount")}</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={t("Discount")} name="discount">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Form.Item label={t("Govt Ref #")} name="govtRefNumber">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={t("Remark")} name="remark">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label={t("Upload Document")}>

                  {/* VIEW MODE */}
                  {readOnly ? (
                    cloudinaryResult ? (
                      <div className="bg-gray-100 p-3 rounded-xl">
                        <a
                          href={cloudinaryResult}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 font-medium"
                        >
                          📄 View Document
                        </a>
                      </div>
                    ) : (
                      <p className="text-gray-400">{t("No document attached")}</p>
                    )
                  ) : (
                    /* CREATE / EDIT MODE */
                    <>
                      {!cloudinaryResult ? (
                        <div className="flex flex-col gap-3">
                          <input
                            type="file"
                            onChange={handleFileChange}
                            className="block w-full text-sm text-gray-600
              file:mr-3 file:py-2 file:px-4 file:rounded-xl
              file:border-0 file:bg-gradient-to-r
              file:from-purple-500 file:to-pink-500 file:text-white"
                          />

                          {uploading && (
                            <div>
                              <p>{t("Uploading")} {Math.round(uploadProgress)}%</p>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-blue-600 h-2 rounded-full"
                                  style={{ width: `${uploadProgress}%` }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex justify-between items-center bg-gray-100 p-3 rounded-xl">
                          <span>{file?.name || "Uploaded file"}</span>
                          <div className="flex gap-2">
                            {!isFileConfirmed && (
                              <Button
                                type="primary"
                                shape="circle"
                                icon={<CheckOutlined />}
                                onClick={() => setIsFileConfirmed(true)}
                              />
                            )}
                            <Button
                              shape="circle"
                              icon={<CloseOutlined />}
                              onClick={() => {
                                resetUpload();
                                setFile(null);
                                setCloudinaryResult(null);
                                setIsFileConfirmed(false);
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </Form.Item>


              </Col>
            </Row>

            {!readOnly && (
              <Form.Item>
                <Button type="primary" htmlType="submit" loading={loading} className="btn-gradient">
                  {t("Save Quotation")}
                </Button>
              </Form.Item>
            )}
          </Form>

        </div>
      </DashLayout>
    </Layout>
  );
};

export default CreateQuotationAntForm;
